import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../AppIcon';

/**
 * High-visibility CTA so real estate agents know HireBroker is for them too.
 * Uses plain Links (not Button asChild) to avoid Radix Slot single-child crashes.
 */
const ProfessionalInviteBanner = ({ className = '', compact = false }) => {
  const outlineBtn =
    'inline-flex items-center justify-center h-9 px-3 rounded-md text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors';
  const primaryBtn =
    'inline-flex items-center justify-center h-9 px-3 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors';
  const outlineBtnLg =
    'inline-flex items-center justify-center gap-2 h-11 px-8 rounded-md text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors';
  const primaryBtnLg =
    'inline-flex items-center justify-center gap-2 h-11 px-8 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors';

  if (compact) {
    return (
      <div
        className={`rounded-xl border border-secondary/25 bg-gradient-to-r from-secondary/10 via-background to-primary/5 p-4 ${className}`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center flex-shrink-0">
              <Icon name="Briefcase" size={20} className="text-secondary" />
            </div>
            <div>
              <p className="font-semibold text-foreground text-sm">
                Real estate agents welcome
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Get verified, respond to user needs, and grow your pipeline.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 sm:flex-shrink-0">
            <Link to="/pro/login" className={outlineBtn}>
              Agent login
            </Link>
            <Link to="/pro/register" className={primaryBtn}>
              Join as Real Estate Agent
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section
      className={`rounded-2xl border border-border overflow-hidden bg-card ${className}`}
    >
      <div className="grid md:grid-cols-2">
        <div className="p-8 md:p-10 flex flex-col justify-center">
          <p className="text-sm font-medium text-secondary mb-2 flex items-center gap-2">
            <Icon name="ShieldCheck" size={16} />
            For Real Estate Agents
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
            Agents, brokers &amp; property advisors can log in too
          </h2>
          <p className="text-muted-foreground mb-6">
            HireBroker is not only for users looking for property. Create a real estate agent account,
            complete verification for a trust badge, and respond to live needs on the notice board.
          </p>
          <ul className="space-y-2 text-sm text-foreground mb-8">
            {[
              'Verified trust badge for clients',
              'Respond to property needs notice board',
              'Built for agents, brokers, and advisors',
            ].map((item) => (
              <li key={item} className="flex items-center gap-2">
                <Icon name="CheckCircle2" size={16} className="text-success flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-3">
            <Link to="/pro/register" className={primaryBtnLg}>
              <Icon name="UserPlus" size={18} />
              Register as Real Estate Agent
            </Link>
            <Link to="/pro/login" className={outlineBtnLg}>
              <Icon name="LogIn" size={18} />
              Real Estate Agent login
            </Link>
          </div>
        </div>
        <div className="bg-gradient-to-br from-secondary/20 via-primary/10 to-muted p-8 md:p-10 flex flex-col justify-center border-t md:border-t-0 md:border-l border-border">
          <div className="grid grid-cols-1 gap-4">
            {[
              { role: 'Property Agent', desc: 'Represent clients and close matches faster', icon: 'UserCheck' },
              { role: 'Broker', desc: 'Facilitate deals with verified credentials', icon: 'Building2' },
              { role: 'Property Advisor', desc: 'Guide investors and seekers with expertise', icon: 'Lightbulb' },
            ].map((card) => (
              <div
                key={card.role}
                className="flex items-center gap-3 rounded-xl bg-background/80 border border-border p-4"
              >
                <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name={card.icon} size={20} className="text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">{card.role}</p>
                  <p className="text-xs text-muted-foreground">{card.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProfessionalInviteBanner;
